import moment from "moment";
import { EsiApiError, esiRequest, nameOf, trimNumber } from "pages/PayRoll/Configuration/StatutoryConfiguration/ESI/esiConfigApi";

// ESI Eligibility APIs (Payroll > Statutory > ESI, screens E1–E4; ESI
// Frontend API Guide sections 12–15). Coverage is decided by the server;
// these mappers only shape it for the list, drawers and IP import.

const text = (value) => (value == null ? "" : String(value));
const formatDay = (value) => (value ? moment(value).format("DD MMM YYYY") : "–");

// IP status chip: OK / MISSING / DUE (+ due date); null -> no chip.
export const ipChip = (status, dueOn) => {
  switch (String(status || "").toUpperCase()) {
    case "OK":
      return { label: "OK", tone: "green" };
    case "MISSING":
      return { label: "Missing", tone: "red" };
    case "DUE":
      return { label: dueOn ? `Due ${moment(dueOn).format("DD MMM")}` : "Due", tone: "amber" };
    default:
      return { label: "", tone: "" };
  }
};

// ESI code with its dashed form (code_display; built when missing).
const dashed = (code) => {
  const digits = String(code || "").replace(/\D/g, "");
  return digits.length === 17 ? `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 10)}-${digits.slice(10, 13)}-${digits.slice(13)}` : text(code);
};
const esiCodeOf = (code) => (code ? { ...code, code_display: text(code.code_display) || dashed(code.code) } : null);
// The employee's site: {id, name, code}.
const siteOf = (site) => (site ? { id: site.id, name: text(site.name), code: text(site.code) } : null);

// One list row (results[] of GET eligibility/, employee of GET employees/<id>/).
export const fromApiRow = (api) => {
  const ip = ipChip(api.ip_status, api.ip_due_on);
  return {
    id: api.employee_id,
    employee_id: api.employee_id,
    name: text(api.name),
    emp_id: text(api.employee_code),
    department: text(api.department),
    code: esiCodeOf(api.esi_code),
    code_label: text(api.esi_code?.branch_name),
    site: siteOf(api.site),
    ip_number: text(api.ip_number),
    ip_label: text(api.ip_number) || "—",
    ip_status_code: text(api.ip_status),
    ip_due_on: text(api.ip_due_on),
    ip,
    ip_status: ip.label,
    wage_at_test: trimNumber(api.wage_at_period_start),
    covered: String(api.coverage || "").toUpperCase() === "COVERED",
    coverage_label: text(api.coverage_label) || (String(api.coverage || "").toUpperCase() === "COVERED" ? "Covered" : "Not covered"),
    reason: text(api.reason),
    covered_until: text(api.covered_until),
    covered_until_label: api.covered_until ? formatDay(api.covered_until) : "—",
    has_override: Boolean(api.has_override),
  };
};

// Periods for the filter: value = the period's first month (YYYY-MM).
export const fetchEligibilityPeriods = async ({ domain, companyId, signal }) => {
  const data = await esiRequest({ domain, path: "eligibility/periods/", query: { company_id: companyId }, signal });
  return (Array.isArray(data) ? data : []).map((period) => ({
    value: moment(period.start).format("YYYY-MM"),
    label: text(period.label) || `${moment(period.start).format("MMM")} – ${moment(period.end).format("MMM YYYY")}`,
    start: text(period.start),
    end: text(period.end),
    isCurrent: Boolean(period.is_current),
  }));
};

// One page of the list; cards follow the search and filters.
export const fetchEligibility = async ({ domain, companyId, period, codeId, card, search, page = 1, pageSize = 50, signal }) => {
  const data = await esiRequest({
    domain,
    path: "eligibility/",
    query: {
      company_id: companyId,
      period,
      code_id: codeId,
      card: card && card !== "all" ? card : undefined,
      search: search || undefined,
      page,
      page_size: pageSize,
    },
    signal,
  });
  return {
    period: data?.period || null,
    configured: data?.configured !== false,
    cards: data?.cards || {},
    rows: (data?.results || []).map(fromApiRow),
    count: Number(data?.count) || 0,
  };
};

// ---- Employee ESI Details (E4 / E2) ----

// history[] -> Coverage History stepper steps ({ label, date, done }).
const historySteps = (history) => {
  const seen = {};
  return (history || []).map((item) => {
    const upcoming = String(item.status || "").toUpperCase() === "UPCOMING";
    let label = text(item.period) || (upcoming ? "Next period" : text(item.label) || formatDay(item.date));
    seen[label] = (seen[label] || 0) + 1;
    if (seen[label] > 1) label = `${label} (${seen[label]})`;
    const coveredFrom = item.covered_from && item.period_start && item.covered_from !== item.period_start ? ` from ${moment(item.covered_from).format("DD MMM")}` : "";
    return { label, date: item.period ? `${text(item.label)}${coveredFrom}` : text(item.label) || formatDay(item.date), done: !upcoming };
  });
};

const overrideOf = (data) => {
  const raw = data.override || data.identifiers?.override || null;
  const status = String(raw?.status || raw?.override_status || "").toUpperCase();
  if (!raw || !status || status === "USE_SYSTEM") return null;
  return {
    status,
    from: text(raw.from ?? raw.override_from),
    reason: text(raw.reason ?? raw.override_reason),
    saved_by: nameOf(raw.saved_by ?? raw.updated_by),
    saved_at: text(raw.saved_at ?? raw.updated_at),
  };
};

export const fromApiEmployee = (data) => {
  const identifiers = data.identifiers || {};
  const coverage = data.coverage || {};
  return {
    row: fromApiRow(data.employee || {}),
    row_version: data.row_version ?? null,
    coverage: {
      period: coverage.period || null,
      wage_at_test: trimNumber(coverage.wage_at_test),
      test_date: text(coverage.test_date),
      current_wage: trimNumber(coverage.current_wage),
      status: String(coverage.status || "").toUpperCase(),
      label: text(coverage.label),
      reason: text(coverage.reason),
      rule_applied: text(coverage.rule_applied),
      covered_from: text(coverage.covered_from),
      covered_from_rule: text(coverage.covered_from_rule),
      covered_until: text(coverage.covered_until),
      covered_until_rule: text(coverage.covered_until_rule),
      notes: text(coverage.notes),
      locked: Boolean(coverage.locked),
    },
    identifiers: {
      ip_number: text(identifiers.ip_number),
      ip_source: text(identifiers.ip_source),
      ip: ipChip(identifiers.ip_status, identifiers.ip_due_on),
      ip_status: text(identifiers.ip_status),
      ip_due_on: text(identifiers.ip_due_on),
      esi_code: esiCodeOf(identifiers.esi_code || data.employee?.esi_code),
      site: siteOf(identifiers.site || data.employee?.site),
      dispensary: text(identifiers.dispensary),
      disability_visible: Boolean(identifiers.disability_visible),
      disability: Boolean(identifiers.disability),
      disability_from: text(identifiers.disability_from),
      disability_evidence_ref: text(identifiers.disability_evidence_ref),
    },
    override: overrideOf(data),
    history: historySteps(data.history),
    alerts: (data.alerts || []).map((alert) => ({
      id: alert.id,
      severity: String(alert.severity || "").toUpperCase(),
      message: text(alert.message),
    })),
  };
};

export const fetchEsiEmployee = async ({ domain, companyId, employeeId, period, signal }) =>
  fromApiEmployee(await esiRequest({ domain, path: `employees/${employeeId}/`, query: { company_id: companyId, period }, signal }));

const EDIT_ERROR_KEYS = { disability_evidence_ref: "disability_evidence" };

// Edit ESI Details: only the sections sent are saved; coverage is
// recalculated by the server. ESI code is read-only (not sent).
export const saveEsiEmployee = async ({ domain, companyId, employeeId, form, rowVersion, disabilityVisible }) => {
  const overriding = form.override_status !== "SYSTEM";
  try {
    const data = await esiRequest({
      domain,
      path: `employees/${employeeId}/`,
      method: "PUT",
      body: {
        company_id: Number(companyId),
        row_version: rowVersion,
        ip_number: form.ip_number || null,
        dispensary: form.dispensary.trim(),
        ...(disabilityVisible
          ? {
              disability: Boolean(form.disability_on),
              ...(form.disability_on ? { disability_from: form.disability_from || null, disability_evidence_ref: form.disability_evidence.trim() } : {}),
            }
          : {}),
        override_status: overriding ? form.override_status : "USE_SYSTEM",
        ...(overriding ? { override_from: form.override_from || null, override_reason: form.override_reason.trim() } : {}),
      },
    });
    return { detail: fromApiEmployee(data.data || {}), message: data.message };
  } catch (err) {
    if (err instanceof EsiApiError && err.fieldErrors) {
      err.fieldErrors = Object.fromEntries(Object.entries(err.fieldErrors).map(([key, message]) => [EDIT_ERROR_KEYS[key] || key, message]));
    }
    throw err;
  }
};

// ---- Import IP numbers ----

// rows: [{ employee_code, ip_number, dispensary?, covered_in_current_period? }].
// dry_run = true only checks. Returns { created, updated, unchanged,
// errors: [{ row (1-based), employee_code, message }] }.
export const importIpNumbers = async ({ domain, companyId, rows, dryRun }) => {
  const data = await esiRequest({
    domain,
    path: "eligibility/import-ip/",
    method: "POST",
    body: { company_id: Number(companyId), dry_run: Boolean(dryRun), rows },
  });
  return {
    created: Number(data?.created) || 0,
    updated: Number(data?.updated) || 0,
    unchanged: Number(data?.unchanged) || 0,
    errors: (data?.errors || []).map((item) => ({ row: Number(item.row), employee_code: text(item.employee_code), message: text(item.message) })),
  };
};
